// Time-of-day sky/sea palette — REALISTIC open-ocean tones (2.5D art pass, 2026-06-26; the old pastel
// Monument-Valley look read as childish). A room is one of three modes:
//   'mint'   — fixed clear-day open ocean (deep blues)
//   'sunset' — fixed golden-hour ocean (warm reflection over dark water)
//   'cycle'  — a slow day→sunset→night→dawn→day loop (the "24h feel")
// In multiplayer the server owns the mode + phase and broadcasts them, so every player in a room sees the
// SAME sky. Units (planes/ships) keep their own identity colors for readability — only the environment shifts.
export type SkyMode = 'mint' | 'sunset' | 'cycle';

type RGB = [number, number, number];
interface Key {
  at: number;          // phase 0..1 where this keyframe sits
  sea: [RGB, RGB, RGB]; // sea gradient: top, mid, bottom
  cloud: RGB;          // cloud body
  shade: RGB;          // cloud shaded underside
  sun: RGB;            // sunlit cloud-crown color (white at noon, gold at sunset, moon-blue at night)
  ground: RGB;         // cloud ground-shadow on the sea
  ambient: [number, number, number, number] | null; // optional full-screen mood overlay (rgba)
  night: number;       // 0 = full day, 1 = deep night (drives the star field)
}

// clear day over open ocean — deep saturated blue, lighter toward the horizon glint
const DAY: Key = {
  at: 0, sea: [[52, 118, 168], [32, 90, 138], [20, 66, 108]],
  cloud: [250, 251, 253], shade: [174, 188, 202], sun: [255, 255, 255], ground: [10, 38, 64], ambient: null, night: 0,
};
// golden hour — warm sun reflection over darkening water (real sunsets darken the sea, not turn it pink)
const SUNSET: Key = {
  at: 0, sea: [[172, 120, 92], [96, 90, 106], [42, 56, 88]],
  cloud: [252, 226, 196], shade: [150, 128, 142], sun: [255, 208, 130], ground: [24, 30, 52], ambient: [255, 150, 80, 0.08], night: 0, // golden crowns, mauve-gray bases
};
// moonlit night — near-black navy with cold gray clouds (dark, but shapes stay readable)
const NIGHT: Key = {
  at: 0, sea: [[18, 30, 54], [13, 22, 42], [9, 15, 30]],
  cloud: [104, 114, 136], shade: [52, 60, 82], sun: [172, 186, 218], ground: [4, 10, 20], ambient: [10, 16, 40, 0.26], night: 1, // cold moonlit rims
};
// cold steel dawn — pale gray-blue light before the sun clears the horizon
const DAWN: Key = {
  at: 0, sea: [[124, 124, 142], [84, 96, 122], [52, 68, 98]],
  cloud: [224, 222, 226], shade: [150, 152, 174], sun: [255, 226, 208], ground: [26, 40, 64], ambient: [190, 150, 150, 0.06], night: 0.22, // pale rose first light
};

// the cycle keyframes, in phase order (wraps from 1.0 back to 0.0 = DAY)
const CYCLE: Key[] = [
  { ...DAY, at: 0.0 },
  { ...SUNSET, at: 0.28 },
  { ...NIGHT, at: 0.5 },
  { ...DAWN, at: 0.8 },
  { ...DAY, at: 1.0 },
];

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpRGB = (a: RGB, b: RGB, t: number): RGB => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const hex = (c: RGB) => `#${c.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
const rgbStr = (c: RGB) => `${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])}`;

export interface ResolvedPalette {
  sea: [string, string, string]; // hex stops
  cloud: string; shade: string; sun: string; ground: string; // "r,g,b" strings (alpha added by the caller)
  ambient: [number, number, number, number] | null;
  night: number;
}

function resolveKey(k: Key): ResolvedPalette {
  return { sea: [hex(k.sea[0]), hex(k.sea[1]), hex(k.sea[2])], cloud: rgbStr(k.cloud), shade: rgbStr(k.shade), sun: rgbStr(k.sun), ground: rgbStr(k.ground), ambient: k.ambient, night: k.night };
}

function blend(a: Key, b: Key, t: number): ResolvedPalette {
  const amb: [number, number, number, number] | null =
    !a.ambient && !b.ambient ? null
    : [lerp(a.ambient?.[0] ?? b.ambient![0], b.ambient?.[0] ?? a.ambient![0], t),
       lerp(a.ambient?.[1] ?? b.ambient![1], b.ambient?.[1] ?? a.ambient![1], t),
       lerp(a.ambient?.[2] ?? b.ambient![2], b.ambient?.[2] ?? a.ambient![2], t),
       lerp(a.ambient?.[3] ?? 0, b.ambient?.[3] ?? 0, t)];
  return {
    sea: [hex(lerpRGB(a.sea[0], b.sea[0], t)), hex(lerpRGB(a.sea[1], b.sea[1], t)), hex(lerpRGB(a.sea[2], b.sea[2], t))],
    cloud: rgbStr(lerpRGB(a.cloud, b.cloud, t)),
    shade: rgbStr(lerpRGB(a.shade, b.shade, t)),
    sun: rgbStr(lerpRGB(a.sun, b.sun, t)),
    ground: rgbStr(lerpRGB(a.ground, b.ground, t)),
    ambient: amb,
    night: lerp(a.night, b.night, t),
  };
}

/** Resolve the current sky palette for a mode + phase (phase only matters for 'cycle', 0..1). */
export function skyPalette(mode: SkyMode, phase: number): ResolvedPalette {
  if (mode === 'mint') return resolveKey(DAY);
  if (mode === 'sunset') return resolveKey(SUNSET);
  const p = ((phase % 1) + 1) % 1;
  for (let i = 0; i < CYCLE.length - 1; i++) {
    const a = CYCLE[i], b = CYCLE[i + 1];
    if (p >= a.at && p <= b.at) return blend(a, b, (p - a.at) / (b.at - a.at));
  }
  return resolveKey(DAY);
}

export const SKY_PERIOD = 540; // seconds for a full day/night cycle (~9 min — a session always SEES sunset/night/dawn)
/** Every room lives the full 24h cycle — day → sunset → night → dawn. The ambience IS the hook;
 *  static skies (old 'mint'/'sunset' modes) hid the best moods from most players. */
export function randomSkyMode(_r: number): SkyMode { return 'cycle'; }
